import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useDebounce } from 'use-debounce';

import BaseModal, { BaseModalProps } from '../BaseModal/BaseModal';
import Button from '../Button';
import Checkbox from '../Checkbox';
import { Copy, Checks } from 'src/common/icons';

import { useAccessToken } from 'src/hooks/useAccessToken';
import { useAlerts } from 'src/common/AlertManager';
import useModal from 'src/hooks/useModal';
import {
  getChannelPresets,
  generateCampaignLinks,
  listCampaignPresets,
  createCampaignPreset,
  updateCampaignPreset,
} from 'src/api/functions/campaignLinksApi';
import { getDistinctValues } from 'src/api/functions/deliveryAnalyticsApi';
import {
  ChannelPreset,
  ChannelPlacement,
  ChannelSelection,
  CampaignLinkRow,
  CampaignLinkWarning,
  CampaignLinkScope,
  CampaignPreset,
  CampaignPresetPayload,
  TagStyle,
} from 'src/types/TCampaignLinks';

import styles from './CampaignLinksModal.module.scss';
import { planUpgradeRequired, TPlanUpgradeRequired } from 'src/api/utils/errorHandlers';
import { PlanUpgradeBanner } from 'src/components/FeatureUnavailable/PlanUpgradeBanner';

type CampaignLinksModalProps = BaseModalProps & {
  // Exactly one scope source.
  assetIds?: string[];
  collectionId?: number | string;
  shareId?: string;
  mode?: 'owner' | 'share';
};

const TAG_STYLE_OPTIONS: { value: TagStyle; label: string }[] = [
  { value: 'fs', label: 'FileSpin (fs_*)' },
  { value: 'utm', label: 'UTM (utm_*)' },
  { value: 'both', label: 'Both' },
];

const IMAGE_FORMAT_OPTIONS = ['webp', 'jpg', 'png', 'avif'];

// Key a placement uniquely within the selection set.
const placementKey = (channel: string, p: ChannelPlacement) => `${channel}::${p.id}`;

// fs_channel validation, mirroring the backend (channel keys become fs_channel).
const CHANNEL_KEY_RE = /^[A-Za-z0-9_-]{1,64}$/;

// Merge user-added channel groups into a base list, deduping placements by id
// and folding custom placements into an existing channel group when the key
// matches. Used both to render server + custom channels together and to absorb
// custom entries that arrive via a saved channel-set preset.
const mergeChannelGroups = (base: ChannelPreset[], extra: ChannelPreset[]): ChannelPreset[] => {
  const map = new Map<string, ChannelPreset>();
  for (const ch of base) map.set(ch.channel, { ...ch, placements: [...ch.placements] });
  for (const ch of extra) {
    const existing = map.get(ch.channel);
    if (existing) {
      const ids = new Set(existing.placements.map((p) => p.id));
      existing.placements = [...existing.placements, ...ch.placements.filter((p) => !ids.has(p.id))];
    } else {
      map.set(ch.channel, { ...ch, placements: [...ch.placements] });
    }
  }
  return Array.from(map.values());
};

function isFetchError(result: any): boolean {
  return result && typeof result.status === 'number' && result.status >= 400;
}

export const CampaignLinksModal: React.FC<CampaignLinksModalProps> = ({
  assetIds,
  collectionId,
  shareId,
  mode = 'owner',
  ...modalProps
}) => {
  const accessToken = useAccessToken();
  const { showAlert, showSuccess } = useAlerts();
  const { closeModal } = useModal();

  // ---- catalog + presets ----
  const [channels, setChannels] = useState<ChannelPreset[]>([]);
  const [planGate, setPlanGate] = useState<TPlanUpgradeRequired | null>(null);
  const [channelLabels, setChannelLabels] = useState<Record<string, string>>({});
  // User-defined channels/sizes layered on top of the server catalog.
  const [customChannels, setCustomChannels] = useState<ChannelPreset[]>([]);
  const [campaignPresets, setCampaignPresets] = useState<CampaignPreset[]>([]);
  const [channelSetPresets, setChannelSetPresets] = useState<CampaignPreset[]>([]);
  // The currently-loaded preset ids (controlled <select> values; '' = none).
  // When set, saving updates that preset rather than creating a duplicate.
  const [loadedCampaignPresetId, setLoadedCampaignPresetId] = useState('');
  const [loadedChannelSetPresetId, setLoadedChannelSetPresetId] = useState('');
  const [campaignSuggestions, setCampaignSuggestions] = useState<string[]>([]);
  const [channelSuggestions, setChannelSuggestions] = useState<string[]>([]);

  // ---- config state ----
  const [campaign, setCampaign] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [variant, setVariant] = useState('');
  const [audience, setAudience] = useState('');
  const [tagStyle, setTagStyle] = useState<TagStyle>('fs');
  // 'auto' omits the format param so the CDN negotiates AVIF/WebP with a JPG
  // fallback per the client's Accept header — best for web-embedded links.
  const [imageFormat, setImageFormat] = useState('auto');
  const [forceDownload, setForceDownload] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [expandedChannels, setExpandedChannels] = useState<Record<string, boolean>>({});

  // ---- custom channel + size form ----
  const [customChannel, setCustomChannel] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [customW, setCustomW] = useState('');
  const [customH, setCustomH] = useState('');
  const [customFormat, setCustomFormat] = useState('');

  // ---- results ----
  const [rows, setRows] = useState<CampaignLinkRow[]>([]);
  const [warnings, setWarnings] = useState<CampaignLinkWarning[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [savingPreset, setSavingPreset] = useState(false);

  const scope: CampaignLinkScope | null = useMemo(() => {
    if (shareId) return { share_id: shareId };
    if (collectionId !== undefined && collectionId !== null) return { collection_id: collectionId };
    if (assetIds && assetIds.length) return { asset_ids: assetIds };
    return null;
  }, [shareId, collectionId, assetIds]);

  // ---- load catalog + presets + autocomplete on mount ----
  useEffect(() => {
    if (!accessToken) return;
    (async () => {
      const cat = await getChannelPresets(accessToken);
      const catGate = planUpgradeRequired(cat);
      if (catGate) {
        // Plan-gated (403): stop loading and show the upsell instead of an
        // empty, functionless modal.
        setPlanGate(catGate);
        return;
      }
      if (!isFetchError(cat) && (cat as any).channels) {
        const list = (cat as any).channels as ChannelPreset[];
        setChannels(list);
        if ((cat as any).channel_labels) setChannelLabels((cat as any).channel_labels);
        // Expand the first channel by default for discoverability.
        if (list.length) setExpandedChannels({ [list[0].channel]: true });
      }
      const cp = await listCampaignPresets(accessToken, 'campaign');
      if (!isFetchError(cp) && (cp as any).presets) setCampaignPresets((cp as any).presets);
      const cs = await listCampaignPresets(accessToken, 'channel_set');
      if (!isFetchError(cs) && (cs as any).presets) setChannelSetPresets((cs as any).presets);

      // Blend distinct-values from delivery analytics for autocomplete.
      const dvCampaign = await getDistinctValues(accessToken, 'campaign_key', { limit: 50 });
      if (!isFetchError(dvCampaign) && (dvCampaign as any).values) {
        setCampaignSuggestions((dvCampaign as any).values);
      }
      const dvChannel = await getDistinctValues(accessToken, 'channel_key', { limit: 50 });
      if (!isFetchError(dvChannel) && (dvChannel as any).values) {
        setChannelSuggestions((dvChannel as any).values);
      }
    })();
  }, [accessToken]);

  // Server catalog + user-defined channels rendered and selected together.
  const allChannels: ChannelPreset[] = useMemo(
    () => mergeChannelGroups(channels, customChannels),
    [channels, customChannels],
  );

  // ---- build the channels payload from the current selection ----
  const selectedChannels: ChannelSelection[] = useMemo(() => {
    const out: ChannelSelection[] = [];
    for (const ch of allChannels) {
      const placements = ch.placements.filter((p) => selected[placementKey(ch.channel, p)]);
      if (placements.length) out.push({ channel: ch.channel, placements });
    }
    return out;
  }, [allChannels, selected]);

  const selectedCount = selectedChannels.reduce((n, c) => n + c.placements.length, 0);

  // ---- debounced live generate ----
  const generatePayload = useMemo(
    () => ({
      scope,
      channels: selectedChannels,
      campaign: campaign || undefined,
      campaign_name: campaignName || undefined,
      variant: variant || undefined,
      audience: audience || undefined,
      tag_style: tagStyle,
      format: imageFormat,
      delivery: forceDownload ? ('download' as const) : null,
    }),
    [scope, selectedChannels, campaign, campaignName, variant, audience, tagStyle, imageFormat, forceDownload],
  );
  const [debouncedPayload] = useDebounce(generatePayload, 350);

  useEffect(() => {
    if (!accessToken || !scope) return;
    if (!debouncedPayload.channels.length) {
      setRows([]);
      setWarnings([]);
      setGenError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsGenerating(true);
      setGenError(null);
      const result = await generateCampaignLinks(debouncedPayload as any, accessToken);
      if (cancelled) return;
      setIsGenerating(false);
      if (isFetchError(result)) {
        const msg = (result as any).error?.message || (result as any).message || 'Failed to generate links';
        setGenError(typeof msg === 'string' ? msg : 'Failed to generate links');
        setRows([]);
        setWarnings([]);
        return;
      }
      setRows((result as any).rows || []);
      setWarnings((result as any).warnings || []);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPayload, accessToken]);

  // ---- selection helpers ----
  const togglePlacement = (channel: string, p: ChannelPlacement) => {
    const key = placementKey(channel, p);
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleChannelAll = (ch: ChannelPreset, on: boolean) => {
    setSelected((prev) => {
      const next = { ...prev };
      ch.placements.forEach((p) => {
        next[placementKey(ch.channel, p)] = on;
      });
      return next;
    });
  };

  const applyCampaignPreset = (presetId: string) => {
    if (!presetId) {
      // "Load saved campaign…" blank option clears the loaded link so the
      // next save creates a new preset.
      setLoadedCampaignPresetId('');
      return;
    }
    const p = campaignPresets.find((x) => String(x.id) === presetId);
    if (!p) return;
    setCampaign(p.payload.campaign || '');
    setCampaignName(p.payload.campaign_name || '');
    setVariant(p.payload.variant || '');
    setAudience(p.payload.audience || '');
    if (p.payload.tag_style) setTagStyle(p.payload.tag_style);
    setLoadedCampaignPresetId(String(p.id));
  };

  const applyChannelSetPreset = (presetId: string) => {
    if (!presetId) {
      setLoadedChannelSetPresetId('');
      return;
    }
    const p = channelSetPresets.find((x) => String(x.id) === presetId);
    if (!p || !p.payload.channels) return;
    const next: Record<string, boolean> = {};
    // Any placement in the preset not present in the catalog is a custom one
    // that must be re-materialised so it renders and stays selected.
    const toAdd: ChannelPreset[] = [];
    p.payload.channels.forEach((c) => {
      const knownIds = new Set(
        (allChannels.find((g) => g.channel === c.channel)?.placements || []).map((pl) => pl.id),
      );
      const missing: ChannelPlacement[] = [];
      c.placements.forEach((pl) => {
        next[placementKey(c.channel, pl)] = true;
        if (!knownIds.has(pl.id)) missing.push(pl);
      });
      if (missing.length) {
        toAdd.push({
          channel: c.channel,
          label: channelLabels[c.channel.toLowerCase()] || c.channel,
          placements: missing,
        });
      }
    });
    if (toAdd.length) {
      setCustomChannels((prev) => mergeChannelGroups(prev, toAdd));
      setExpandedChannels((prev) => {
        const e = { ...prev };
        toAdd.forEach((c) => (e[c.channel] = true));
        return e;
      });
    }
    setSelected(next);
    setLoadedChannelSetPresetId(String(p.id));
  };

  // Add a user-defined channel/size into the selection (image only).
  const addCustomPlacement = () => {
    const key = customChannel.trim();
    if (!CHANNEL_KEY_RE.test(key)) {
      showAlert('Channel key must match A–Z, 0–9, _ or - (max 64 chars)');
      return;
    }
    const w = parseInt(customW, 10);
    const h = parseInt(customH, 10);
    if (!w || !h || w <= 0 || h <= 0) {
      showAlert('Enter a valid width and height');
      return;
    }
    const label = customLabel.trim() || `${w}×${h}`;
    const placement: ChannelPlacement = {
      id: `custom-${key}-${w}x${h}-${Date.now()}`,
      label,
      media: 'image',
      w,
      h,
    };
    if (customFormat) placement.format = customFormat;
    setCustomChannels((prev) =>
      mergeChannelGroups(prev, [
        { channel: key, label: channelLabels[key.toLowerCase()] || key, placements: [placement] },
      ]),
    );
    setSelected((prev) => ({ ...prev, [placementKey(key, placement)]: true }));
    setExpandedChannels((prev) => ({ ...prev, [key]: true }));
    // Keep the channel key so the user can add more sizes to it quickly.
    setCustomLabel('');
    setCustomW('');
    setCustomH('');
  };

  // ---- save presets ----
  const campaignPayload = (): CampaignPresetPayload => ({
    campaign: campaign || undefined,
    campaign_name: campaignName || undefined,
    variant: variant || undefined,
    audience: audience || undefined,
    tag_style: tagStyle,
  });

  const refreshCampaignPresets = async () => {
    if (!accessToken) return;
    const cp = await listCampaignPresets(accessToken, 'campaign');
    if (!isFetchError(cp) && (cp as any).presets) setCampaignPresets((cp as any).presets);
  };

  const refreshChannelSetPresets = async () => {
    if (!accessToken) return;
    const cs = await listCampaignPresets(accessToken, 'channel_set');
    if (!isFetchError(cs) && (cs as any).presets) setChannelSetPresets((cs as any).presets);
  };

  // Update the currently-loaded campaign preset (PUT), keeping it loaded.
  const handleSaveCampaign = async () => {
    if (!accessToken) return;
    const loaded = campaignPresets.find((x) => String(x.id) === loadedCampaignPresetId);
    if (!loaded) {
      // No preset loaded — fall back to creating a new one.
      return handleSaveCampaignAsNew();
    }
    const name = window.prompt('Rename / update campaign preset', loaded.name);
    if (!name) return;
    setSavingPreset(true);
    const result = await updateCampaignPreset(
      loaded.id,
      { kind: 'campaign', name, payload: campaignPayload() },
      accessToken,
    );
    setSavingPreset(false);
    if (isFetchError(result)) {
      showAlert('Could not update campaign preset');
      return;
    }
    showSuccess('Campaign preset updated');
    await refreshCampaignPresets();
  };

  // Always create a fresh campaign preset (POST), forking from any loaded one.
  const handleSaveCampaignAsNew = async () => {
    if (!accessToken) return;
    const name = window.prompt('Name this campaign preset', campaignName || campaign);
    if (!name) return;
    setSavingPreset(true);
    const result = await createCampaignPreset(
      { kind: 'campaign', name, payload: campaignPayload() },
      accessToken,
    );
    setSavingPreset(false);
    if (isFetchError(result)) {
      showAlert('Could not save campaign preset');
      return;
    }
    showSuccess('Campaign preset saved');
    // Switch to the newly-created preset so subsequent saves update it.
    const created = (result as any).preset as CampaignPreset | undefined;
    if (created?.id) setLoadedCampaignPresetId(String(created.id));
    await refreshCampaignPresets();
  };

  // Update the currently-loaded channel-set preset (PUT), keeping it loaded.
  const handleSaveChannelSet = async () => {
    if (!accessToken) return;
    if (!selectedChannels.length) {
      showAlert('Select at least one channel/size first');
      return;
    }
    const loaded = channelSetPresets.find((x) => String(x.id) === loadedChannelSetPresetId);
    if (!loaded) {
      return handleSaveChannelSetAsNew();
    }
    const name = window.prompt('Rename / update channel set', loaded.name);
    if (!name) return;
    setSavingPreset(true);
    const result = await updateCampaignPreset(
      loaded.id,
      { kind: 'channel_set', name, payload: { channels: selectedChannels } },
      accessToken,
    );
    setSavingPreset(false);
    if (isFetchError(result)) {
      showAlert('Could not update channel set');
      return;
    }
    showSuccess('Channel set updated');
    await refreshChannelSetPresets();
  };

  // Always create a fresh channel-set preset (POST), forking from any loaded one.
  const handleSaveChannelSetAsNew = async () => {
    if (!accessToken) return;
    if (!selectedChannels.length) {
      showAlert('Select at least one channel/size first');
      return;
    }
    const name = window.prompt('Name this channel set');
    if (!name) return;
    setSavingPreset(true);
    const result = await createCampaignPreset(
      { kind: 'channel_set', name, payload: { channels: selectedChannels } },
      accessToken,
    );
    setSavingPreset(false);
    if (isFetchError(result)) {
      showAlert('Could not save channel set');
      return;
    }
    showSuccess('Channel set saved');
    const created = (result as any).preset as CampaignPreset | undefined;
    if (created?.id) setLoadedChannelSetPresetId(String(created.id));
    await refreshChannelSetPresets();
  };

  // ---- export ----
  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess('Copied to clipboard');
    } catch {
      showAlert('Failed to copy');
    }
  };

  const csv = useMemo(() => {
    const header = ['asset_name', 'asset_id', 'channel', 'placement', 'width', 'height', 'format', 'media', 'url'];
    const escape = (v: any) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = rows.map((r) =>
      [r.asset_name, r.asset_id, r.channel_label, r.placement, r.width, r.height, r.format, r.media, r.url]
        .map(escape)
        .join(','),
    );
    return [header.join(','), ...lines].join('\n');
  }, [rows]);

  const downloadCsv = () => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `campaign-links-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const copyAllUrls = () => copyText(rows.map((r) => r.url).join('\n'));
  const copyJson = () => copyText(JSON.stringify(rows, null, 2));

  const scopeLabel = shareId
    ? 'this share'
    : collectionId !== undefined && collectionId !== null
    ? 'this collection'
    : `${assetIds?.length || 0} asset${(assetIds?.length || 0) !== 1 ? 's' : ''}`;

  const campaignDatalist = useMemo(() => {
    const set = new Set<string>([
      ...campaignPresets.map((p) => p.payload.campaign || '').filter(Boolean),
      ...campaignSuggestions,
    ]);
    return Array.from(set);
  }, [campaignPresets, campaignSuggestions]);

  const channelDatalist = useMemo(() => {
    const set = new Set<string>([
      ...channels.map((c) => c.channel),
      ...Object.keys(channelLabels),
      ...channelSuggestions,
    ]);
    return Array.from(set).filter(Boolean);
  }, [channels, channelLabels, channelSuggestions]);

  return (
    <BaseModal {...modalProps} containerStyle={{ width: 'min(1100px, 94vw)', maxWidth: '94vw' }}>
      <h3 className={styles.title}>Get campaign links</h3>
      {planGate ? (
        <PlanUpgradeBanner gate={planGate} />
      ) : (
      <>
      <p className={styles.subtitle}>
        Generate tagged CDN delivery URLs for {scopeLabel}. Links carry campaign/channel tracking
        ({tagStyle === 'utm' ? 'utm_*' : tagStyle === 'both' ? 'fs_* + utm_*' : 'fs_*'}) so they show up in
        Delivery Analytics.
      </p>

      <div className={styles.twoPane}>
        {/* ---------------- LEFT: config ---------------- */}
        <div className={styles.config}>
          {/* Campaign */}
          <section className={styles.section}>
            <div className={styles.sectionHead}>
              <span className={styles.sectionTitle}>Campaign</span>
              {loadedCampaignPresetId ? (
                <span className={styles.headActions}>
                  <button className={styles.linkBtn} onClick={handleSaveCampaign} disabled={savingPreset}>
                    Update campaign
                  </button>
                  <button className={styles.linkBtn} onClick={handleSaveCampaignAsNew} disabled={savingPreset}>
                    Save as new
                  </button>
                </span>
              ) : (
                <button className={styles.linkBtn} onClick={handleSaveCampaignAsNew} disabled={savingPreset}>
                  Save as campaign
                </button>
              )}
            </div>

            {campaignPresets.length > 0 && (
              <select
                className={styles.select}
                value={loadedCampaignPresetId}
                onChange={(e) => applyCampaignPreset(e.target.value)}
              >
                <option value="">Load saved campaign…</option>
                {campaignPresets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}

            <label className={styles.fieldLabel}>Campaign ID (fs_campaign_id)</label>
            <input
              className={styles.input}
              list="campaign-suggestions"
              placeholder="e.g. spring-launch-2026"
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
            />
            <datalist id="campaign-suggestions">
              {campaignDatalist.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>

            {(tagStyle === 'utm' || tagStyle === 'both') && (
              <>
                <label className={styles.fieldLabel}>Campaign name (utm_campaign)</label>
                <input
                  className={styles.input}
                  placeholder="e.g. Spring Launch 2026"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                />
              </>
            )}

            <div className={styles.fieldRow}>
              <div className={styles.fieldCol}>
                <label className={styles.fieldLabel}>Variant</label>
                <input
                  className={styles.input}
                  placeholder="hero-a"
                  value={variant}
                  onChange={(e) => setVariant(e.target.value)}
                />
              </div>
              <div className={styles.fieldCol}>
                <label className={styles.fieldLabel}>Audience</label>
                <input
                  className={styles.input}
                  placeholder="enterprise"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                />
              </div>
            </div>

            <label className={styles.fieldLabel}>Tagging style</label>
            <div className={styles.radioRow}>
              {TAG_STYLE_OPTIONS.map((opt) => (
                <label key={opt.value} className={styles.radioItem}>
                  <Checkbox radio checked={tagStyle === opt.value} onChange={() => setTagStyle(opt.value)} />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </section>

          {/* Channels + sizes */}
          <section className={styles.section}>
            <div className={styles.sectionHead}>
              <span className={styles.sectionTitle}>Channels &amp; sizes</span>
              {loadedChannelSetPresetId ? (
                <span className={styles.headActions}>
                  <button className={styles.linkBtn} onClick={handleSaveChannelSet} disabled={savingPreset}>
                    Update channel set
                  </button>
                  <button className={styles.linkBtn} onClick={handleSaveChannelSetAsNew} disabled={savingPreset}>
                    Save as new
                  </button>
                </span>
              ) : (
                <button className={styles.linkBtn} onClick={handleSaveChannelSet} disabled={savingPreset}>
                  Save channel set
                </button>
              )}
            </div>

            {channelSetPresets.length > 0 && (
              <select
                className={styles.select}
                value={loadedChannelSetPresetId}
                onChange={(e) => applyChannelSetPreset(e.target.value)}
              >
                <option value="">Load saved channel set…</option>
                {channelSetPresets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}

            <div className={styles.channelList}>
              {allChannels.map((ch) => {
                const allOn = ch.placements.every((p) => selected[placementKey(ch.channel, p)]);
                const expanded = !!expandedChannels[ch.channel];
                return (
                  <div key={ch.channel} className={styles.channel}>
                    <div className={styles.channelHead}>
                      <button
                        className={styles.channelToggle}
                        onClick={() =>
                          setExpandedChannels((prev) => ({ ...prev, [ch.channel]: !prev[ch.channel] }))
                        }
                      >
                        {expanded ? '▾' : '▸'} {ch.label}
                      </button>
                      <button className={styles.linkBtn} onClick={() => toggleChannelAll(ch, !allOn)}>
                        {allOn ? 'Clear' : 'All'}
                      </button>
                    </div>
                    {expanded && (
                      <div className={styles.placementGrid}>
                        {ch.placements.map((p) => (
                          <label key={p.id} className={styles.placement}>
                            <Checkbox
                              checked={!!selected[placementKey(ch.channel, p)]}
                              onChange={() => togglePlacement(ch.channel, p)}
                            />
                            <span className={styles.placementLabel}>
                              {p.label}
                              <span className={styles.placementMeta}>
                                {p.media === 'video'
                                  ? `video · ${p.rendition}`
                                  : `${p.w}×${p.h}${p.format ? ` · ${p.format}` : ''}`}
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Custom channel + size */}
            <div className={styles.customAdd}>
              <span className={styles.customAddTitle}>Add custom channel &amp; size</span>
              <div className={styles.fieldRow}>
                <div className={styles.fieldCol}>
                  <label className={styles.fieldLabel}>Channel key</label>
                  <input
                    className={styles.input}
                    list="channel-suggestions"
                    placeholder="e.g. newsletter"
                    value={customChannel}
                    onChange={(e) => setCustomChannel(e.target.value)}
                  />
                  <datalist id="channel-suggestions">
                    {channelDatalist.map((v) => (
                      <option key={v} value={v} />
                    ))}
                  </datalist>
                </div>
                <div className={styles.fieldCol}>
                  <label className={styles.fieldLabel}>Size label</label>
                  <input
                    className={styles.input}
                    placeholder="e.g. Banner"
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.fieldCol}>
                  <label className={styles.fieldLabel}>Width</label>
                  <input
                    className={styles.input}
                    type="number"
                    min={1}
                    placeholder="1200"
                    value={customW}
                    onChange={(e) => setCustomW(e.target.value)}
                  />
                </div>
                <div className={styles.fieldCol}>
                  <label className={styles.fieldLabel}>Height</label>
                  <input
                    className={styles.input}
                    type="number"
                    min={1}
                    placeholder="600"
                    value={customH}
                    onChange={(e) => setCustomH(e.target.value)}
                  />
                </div>
                <div className={styles.fieldCol}>
                  <label className={styles.fieldLabel}>Format</label>
                  <select
                    className={styles.select}
                    value={customFormat}
                    onChange={(e) => setCustomFormat(e.target.value)}
                  >
                    <option value="">Default</option>
                    {IMAGE_FORMAT_OPTIONS.map((f) => (
                      <option key={f} value={f}>
                        {f.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Button mode="tertiary" size="small" onClick={addCustomPlacement}>
                  + Add size
                </Button>
              </div>
            </div>
          </section>

          {/* Format + delivery */}
          <section className={styles.section}>
            <span className={styles.sectionTitle}>Format &amp; delivery</span>
            <label className={styles.fieldLabel}>Image format</label>
            <select className={styles.select} value={imageFormat} onChange={(e) => setImageFormat(e.target.value)}>
              <option value="auto">Auto (WebP/AVIF, JPG fallback)</option>
              {IMAGE_FORMAT_OPTIONS.map((f) => (
                <option key={f} value={f}>
                  {f.toUpperCase()}
                </option>
              ))}
            </select>
            <p className={styles.fieldHint}>
              Auto serves WebP/AVIF to browsers that support them and falls back to JPG — best for links
              embedded in web pages. Choose <strong>JPG</strong> for downloads and non-web destinations
              (email, ads, social uploads), where WebP/AVIF may not be supported.
            </p>
            <label className={styles.radioItem}>
              <Checkbox checked={forceDownload} onChange={(e) => setForceDownload(e.target.checked)} />
              <span>Force download (attachment)</span>
            </label>
          </section>
        </div>

        {/* ---------------- RIGHT: results ---------------- */}
        <div className={styles.results}>
          <div className={styles.resultsHead}>
            <span className={styles.resultsCount}>
              {isGenerating ? 'Generating…' : `${rows.length} link${rows.length !== 1 ? 's' : ''}`}
              {selectedCount === 0 && !isGenerating ? ' — pick channels & sizes' : ''}
            </span>
            <div className={styles.resultsActions}>
              <Button mode="tertiary" size="small" onClick={copyAllUrls} disabled={!rows.length}>
                Copy all
              </Button>
              <Button mode="tertiary" size="small" onClick={copyJson} disabled={!rows.length}>
                Copy JSON
              </Button>
              <Button mode="primary" size="small" onClick={downloadCsv} disabled={!rows.length}>
                Download CSV
              </Button>
            </div>
          </div>

          {genError && <div className={styles.errorBanner}>{genError}</div>}

          {warnings.length > 0 && (
            <div className={styles.warnings}>
              {warnings.map((w, i) => (
                <div key={i} className={styles.warning}>
                  {w.reason === 'transcode_unavailable' ? (
                    <>
                      <strong>{w.asset_name || w.asset_id}</strong>: {w.requested} transcode not available.
                      {w.available && w.available.length
                        ? ` Available: ${w.available.join(', ')}.`
                        : ' No transcodes yet.'}{' '}
                      Generate the transcode first.
                    </>
                  ) : w.reason === 'transcode_not_public' ? (
                    <>
                      <strong>{w.asset_name || w.asset_id}</strong>: the {w.requested || w.format} video transcode is not public — the link needs a signed URL or the transcode made public.
                    </>
                  ) : w.reason === 'row_cap_reached' ? (
                    <>Row cap reached ({w.cap}). Narrow the selection.</>
                  ) : (
                    <>{w.detail || w.reason}</>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Channel</th>
                  <th>Placement</th>
                  <th>Size / rendition</th>
                  <th>URL</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.asset_id}-${r.channel}-${r.placement}-${i}`}>
                    <td className={styles.assetCell} title={r.asset_id}>
                      {r.asset_name}
                    </td>
                    <td>{r.channel_label}</td>
                    <td>{r.placement}</td>
                    <td>{r.media === 'video' ? r.format : `${r.width}×${r.height} ${r.format}`}</td>
                    <td className={styles.urlCell}>
                      <a href={r.url} target="_blank" rel="noreferrer">
                        {r.url}
                      </a>
                    </td>
                    <td>
                      <button className={styles.iconBtn} title="Copy URL" onClick={() => copyText(r.url)}>
                        <Copy />
                      </button>
                    </td>
                  </tr>
                ))}
                {!rows.length && !isGenerating && (
                  <tr>
                    <td colSpan={6} className={styles.emptyRow}>
                      {selectedCount === 0
                        ? 'Select channels and sizes on the left to preview links here.'
                        : 'No links produced for the current selection.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <Button mode="tertiary" size="small" onClick={closeModal}>
          <Checks /> Done
        </Button>
      </div>
    </>
      )}
    </BaseModal>
  );
};

export default CampaignLinksModal;
